import { Container, Grid2, TextField, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { default as FilmReleaseSelect2 } from "components/global/FilmReleaseSelect";

import ConfirmDialog from "components/common/ConfirmDialog";
import { SMALL_DEVICE_MEDIA_SIZE } from "components/common/constants";
import { ACTION_STATES } from "core/actions";
import log from "loglevel";
import FilmReleaseService from "services/filmReleaseService";

import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import TextAreaEditor from "components/common/TextAreaEditor";
import TypeAssociationSwitch from "components/common/TypeAssociationSwitch";
import { FilmExpandHeader, getContentByFilmRelease } from "components/film/FilmCardView";
import "./styles.scss";
import { formatDate, stringToDate } from "components/common/dateUtils";
import { DATE_FORMAT_SHORT, DATE_FORMAT_ZONE_TIME_LONG } from "../../common/dateUtils";
import ResponsiveDatePicker from "../../common/form/ResponsiveDatePicker";

function RelatedForm(props, ref) {
    const { addStatus, editStatus, editing, onAdd, onEdit, initValues, advId, changeAssociations, associations } =
        props;

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [rating, setRating] = useState("");
    const [ratingReason, setRatingReason] = useState("");
    const bagRef = useRef();
    const privateNoteRef = useRef();
    let formChanged = false;

    useEffect(() => {
        if (initValues && initValues.filmReleaseID && typeof initValues.filmReleaseID !== "number") {
            setRating(initValues.filmReleaseID.filmRatingTypeId.name);
            setRatingReason(initValues.filmReleaseID.ratingReason);
        }
    }, [initValues]);

    useImperativeHandle(ref, () => ({
        submitForm() {
            bagRef.current.submitForm();
        },
    }));

    const handleConfirmDialogClose = (resetForm, submitForm) => {
        setConfirmDialogOpen(false);
        formChanged = false;
        if (submitForm) submitForm();
    };

    const onTextAreaChanged = async () => {
        if (editing && !formChanged) formChanged = true;
    };

    const loadRating = async (filmRelease) => {
        try {
            const { data } = await FilmReleaseService.getFilmRelease(filmRelease.id);
            setRating(data.filmRatingTypeId.name);
            setRatingReason(data.ratingReason);
        } catch (error) {
            log.error(error);
        }
    };

    const onFilmReleaseChange = (option, setFieldValue) => {
        setFieldValue("filmRelease", option);
        loadRating(option);
    };

    function handleOnSubmit(values, { resetForm }) {
        let time = null;
        if (values.time) {
            if (values.date) {
                time = stringToDate(formatDate(values.date, DATE_FORMAT_ZONE_TIME_LONG));
                time.setHours(values.time.getHours(), values.time.getMinutes());
            }
        }
        const body = {
            advertisementID: advId,
            filmReleaseID: values.filmRelease.value,
            dateOccurred: values.date,
            timeOccurred: time,
            advertisementFilmReleaseAssociationTypeID: values.associationType,
            location: values.location,
            entryText: values.comments,
            internalComment: privateNoteRef.current.getValue(),
        };

        const addCallBack = (film) => {
            changeAssociations([...associations, { ...film }]);
            resetForm();
        };

        const editCallBack = (film) => {
            changeAssociations([...associations.filter((association) => association.id !== film.id), { ...film }]);
            resetForm();
        };

        log.debug("body", body);
        if (!editing) onAdd(body, addCallBack);
        else onEdit(body, editCallBack);
    }

    return (
        (<Formik
            innerRef={bagRef}
            enableReinitialize
            initialValues={{
                filmRelease: initValues && initValues.filmReleaseID,
                associationType: initValues && initValues.advertisementFilmReleaseAssociationTypeID,
                date: initValues && initValues.dateOccurred ? stringToDate(formatDate(initValues.dateOccurred, DATE_FORMAT_SHORT)) : null,
                time: initValues && stringToDate(formatDate(initValues.timeOccurred, DATE_FORMAT_ZONE_TIME_LONG)),
                location: initValues && initValues.location,
                comments: initValues && initValues.entryText,
                privateNotes: (initValues && initValues.internalComment) || "",
            }}
            validate={(values) => {
                if (editing && !formChanged) {
                    formChanged = true;
                }

                const requiredMsg = "This field is required";
                const errors = {};
                if (!values.filmRelease) errors.filmRelease = requiredMsg;
                if (!values.associationType) errors.associationType = requiredMsg;
                if (!values.date) errors.date = requiredMsg;
                if (!values.time) errors.time = requiredMsg;
                return errors;
            }}
            onSubmit={handleOnSubmit}
        >
            {({ submitForm, resetForm, values }) => {
                const content = getContentByFilmRelease(values.filmRelease, ratingReason, rating);
                return (
                    (<React.Fragment>
                        {(addStatus === ACTION_STATES.SEND || editStatus === ACTION_STATES.SEND) && (
                            <Backdrop style={{ color: "#fff", zIndex: 10 }} open={true}>
                                <CircularProgress color="inherit" />
                            </Backdrop>
                        )}
                        {confirmDialogOpen && (
                            <ConfirmDialog
                                open={confirmDialogOpen}
                                message="You made changes to the advertisement"
                                confirmText="Save"
                                closeText="Discard"
                                onConfirm={() => handleConfirmDialogClose(resetForm, submitForm)}
                                onCancel={() => handleConfirmDialogClose(resetForm)}
                            />
                        )}
                        <FormContainer>
                            <div style={{ padding: 30, position: "relative" }}>
                                <Form>
                                    <Grid2 id="add-related-film-form" container spacing={2}>
                                        <Grid2
                                            container
                                            item
                                            style={{ alignContent: "baseline", alignItems: "baseline" }}
                                            size={12}
                                        >
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Film Title
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    item
                                                    className="field"
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <Field name="filmRelease">
                                                        {({ field, form }) => {
                                                            // eslint-disable-next-line no-unused-vars
                                                            const { onChange, ...restField } = field;
                                                            const { touched, errors } = form;
                                                            return (
                                                                <React.Fragment>
                                                                    <FilmReleaseSelect2
                                                                        placeholder="Select film title"
                                                                        onChange={(option) => onFilmReleaseChange(option, form.setFieldValue)}
                                                                        {...restField}
                                                                        formikBag={form}
                                                                    />
                                                                    { }
                                                                    {errors.filmRelease && touched.filmRelease && (
                                                                        <small className="error">{errors.filmRelease}</small>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}></Grid2>
                                                <Grid2
                                                    item
                                                    className="field"
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    {content && (
                                                        <FilmExpandHeader

                                                            showRatingAlways
                                                            content={content}
                                                            renderFilm
                                                            listItem={false}
                                                        />
                                                    )}
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Type Association
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    item
                                                    className="field"
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <Field name="associationType">
                                                        {({ field, form: { touched, errors, setFieldValue } }) => {
                                                            // eslint-disable-next-line no-unused-vars
                                                            const { onChange, ...restField } = field;
                                                            return (
                                                                <React.Fragment>
                                                                    <TypeAssociationSwitch
                                                                        onChange={(option) => {
                                                                            setFieldValue("associationType", option);
                                                                        }}
                                                                        {...restField}
                                                                    />
                                                                    {errors.associationType &&
                                                                        touched.associationType && (
                                                                            <small className="error">
                                                                                {errors.associationType}
                                                                            </small>
                                                                        )}
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Incident date
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    item
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <div>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                            <Field name="date">
                                                                {({ field, form: { touched, errors, setFieldValue } }) => {
                                                                    return (
                                                                        <React.Fragment>
                                                                            <ResponsiveDatePicker
                                                                                inputVariant="outlined"
                                                                                variant="inline"
                                                                                margin="normal"
                                                                                value={field.value}
                                                                                autoOk={false}
                                                                                onChange={(date) => setFieldValue("date", date)}
                                                                                KeyboardButtonProps={{
                                                                                    "aria-label": "change date",
                                                                                }}
                                                                            />
                                                                            {errors.date &&
                                                                                touched.date && (
                                                                                    <small style={{color: "red"}}>
                                                                                        {errors.date}
                                                                                    </small>
                                                                                )}
                                                                        </React.Fragment>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </LocalizationProvider>
                                                    </div>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Incident time
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    item
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <div>
                                                        <Field name="time">
                                                            {({ field, form: { touched, errors, setFieldValue } }) => {
                                                                return (
                                                                    <React.Fragment>
                                                                        <LocalizationProvider
                                                                            dateAdapter={AdapterDateFns}
                                                                        >
                                                                            <TimePicker
                                                                                margin="normal"
                                                                                value={field.value}
                                                                                onChange={(value) => {
                                                                                    setFieldValue("time", value);
                                                                                }}
                                                                                KeyboardButtonProps={{
                                                                                    "aria-label": "change time",
                                                                                }}
                                                                                inputVariant="outlined"
                                                                                mask="__:__ _M"
                                                                                keyboardIcon={null}
                                                                            />
                                                                            {errors.time &&
                                                                                touched.time && (
                                                                                    <small style={{color: "red"}}>
                                                                                        {errors.time}
                                                                                    </small>
                                                                                )}
                                                                        </LocalizationProvider>
                                                                    </React.Fragment>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label">
                                                        Location
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    item
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <Field name="location">
                                                        {({ field }) => {
                                                            return <TextField variant={"outlined"} {...field} />;
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography variant={"subtitle2"} className="field-label on-top">
                                                        Comments
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    item
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <Field name="comments">
                                                        {({ field }) => {
                                                            return (
                                                                <React.Fragment>
                                                                    <TextField
                                                                        className="form-control"
                                                                        margin="normal"
                                                                        variant="outlined"
                                                                        multiline
                                                                        rows="4"
                                                                        {...field}
                                                                    />
                                                                    { }
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 container item size={12}>
                                                <Grid2
                                                    item
                                                    className="field-label-wrapper"
                                                    size={{
                                                        xs: 12,
                                                        md: 2
                                                    }}>
                                                    <Typography
                                                        variant={"subtitle2"}
                                                        className="field-label"
                                                        style={{ alignSelf: "unset", paddingTop: 15 }}
                                                    >
                                                        Private Notes
                                                    </Typography>
                                                </Grid2>
                                                <Grid2
                                                    item
                                                    size={{
                                                        xs: 12,
                                                        md: 10
                                                    }}>
                                                    <Field name="privateNotes">
                                                        {({ field }) => {
                                                            return (
                                                                <React.Fragment>
                                                                    <TextAreaEditor
                                                                        ref={privateNoteRef}
                                                                        rootStyle={{ paddingBottom: 10, marginTop: 5 }}
                                                                        defaultValue={field.value}
                                                                        reportChange={onTextAreaChanged}
                                                                    />
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid2>
                                            </Grid2>
                                        </Grid2>
                                    </Grid2>
                                </Form>
                            </div>
                        </FormContainer>
                    </React.Fragment>)
                );
            }}
        </Formik>)
    );
}

function FormContainer({ children }) {
    const theme = useTheme();
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(SMALL_DEVICE_MEDIA_SIZE));
    if (isSmallDevice) {
        return <React.Fragment>{children}</React.Fragment>;
    }
    return <Container maxWidth="lg">{children}</Container>;
}

export default forwardRef(RelatedForm);
